import { useState, useRef, useEffect } from "react";
import { Globe, ChevronDown, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "../contexts/LanguageContext";
import { LANGUAGES } from "../data/translations";

export default function LanguageSwitcher() {
  const { lang, setLanguage } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const currentLang = LANGUAGES.find(l => l.code === lang) || LANGUAGES[0];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={ref} data-testid="language-switcher">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-sm font-body font-medium text-white/70 hover:text-white transition-all"
        style={{
          background: "rgba(255,255,255,0.06)",
          border: "1px solid rgba(255,255,255,0.08)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
        }}
        data-testid="language-switcher-btn"
        aria-label={`Change language, current: ${currentLang.name}`}
        title={currentLang.name}
      >
        <Globe size={14} className="text-saffron flex-shrink-0" />
        {/* Short 2-letter code up to xl; full native name only on very wide screens */}
        <span className="hidden sm:inline xl:hidden text-xs uppercase tracking-wider">
          {currentLang.code}
        </span>
        <span className="hidden xl:inline max-w-[110px] truncate">
          {currentLang.nativeName}
        </span>
        <ChevronDown size={11} className={`transition-transform flex-shrink-0 ${open ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-56 max-h-80 overflow-y-auto rounded-xl border border-white/10 bg-stone-900/98 backdrop-blur-xl shadow-2xl z-50"
            style={{ scrollbarWidth: "thin" }}
            data-testid="language-dropdown"
          >
            <div className="p-2">
              <div className="px-3 py-2 text-xs text-white/30 uppercase tracking-widest font-body">
                Select Language
              </div>
              {LANGUAGES.map((l) => (
                <button
                  key={l.code}
                  onClick={() => {
                    setLanguage(l.code);
                    setOpen(false);
                  }}
                  data-testid={`lang-option-${l.code}`}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-left transition-all ${
                    lang === l.code
                      ? "bg-saffron/15 text-saffron"
                      : "text-white/70 hover:bg-white/5 hover:text-white"
                  }`}
                >
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">{l.nativeName}</span>
                    <span className="text-xs text-white/40">{l.name}</span>
                  </div>
                  {lang === l.code && <Check size={14} className="text-saffron" />}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
